import {
  type PrivateFieldDef,
  type PrivateFormData,
  getPrivateFieldOptions,
  isPrivateFieldFilled,
  privateFieldsForGender,
} from '@/entities/profile';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { useTranslate } from '@tolgee/react';
import { Lock } from 'lucide-react';
import { useEffect } from 'react';
import { MultiCheckField, RequiredMark, SelectField } from './fields';

interface StepPrivateProps {
  data: PrivateFormData;
  gender: number; // 1 = male, 2 = female
  onChange: (data: Partial<PrivateFormData>) => void;
  onValidChange: (isValid: boolean) => void;
}

/** Legacy step8: the private profile (visible only after an accepted sharing). */
export function StepPrivate({ data, gender, onChange, onValidChange }: StepPrivateProps) {
  const { t } = useTranslate();
  const fields = privateFieldsForGender(gender);

  // biome-ignore lint/correctness/useExhaustiveDependencies: onValidChange is stable from parent
  useEffect(() => {
    onValidChange(fields.every((f) => !f.required || isPrivateFieldFilled(data[f.name])));
  }, [data, gender]);

  const renderField = (field: PrivateFieldDef) => {
    const label = t(`profile.privateFields.${field.name}.label`);
    const value = data[field.name];

    if (field.kind === 'text') {
      return (
        <div key={field.name} className="space-y-2">
          <Label htmlFor={`private-${field.name}`}>
            {label}
            <RequiredMark required={field.required} />
          </Label>
          <Textarea
            id={`private-${field.name}`}
            rows={3}
            maxLength={2000}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange({ [field.name]: e.target.value })}
            required={field.required}
          />
        </div>
      );
    }

    if (field.kind === 'multi') {
      return (
        <MultiCheckField
          key={field.name}
          id={field.name}
          label={label}
          options={getPrivateFieldOptions(t, field)}
          values={Array.isArray(value) ? value : []}
          onChange={(values) => onChange({ [field.name]: values })}
          required={field.required}
        />
      );
    }

    return (
      <SelectField
        key={field.name}
        id={field.name}
        label={label}
        options={getPrivateFieldOptions(t, field)}
        value={typeof value === 'string' ? value : undefined}
        onChange={(v) => onChange({ [field.name]: String(v) })}
        required={field.required}
      />
    );
  };

  return (
    <div className="space-y-4">
      <Alert className="border-primary/50 bg-primary/5">
        <Lock className="h-4 w-4" />
        <AlertDescription>{t('signup.hints.privateIntro')}</AlertDescription>
      </Alert>
      {fields.map(renderField)}
      <p className="text-xs text-muted-foreground">{t('signup.hints.required')}</p>
    </div>
  );
}
